import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Star,
  Clock,
  Users,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Loader,
  BookOpen,
} from "lucide-react";
import { coursesApi } from "../lib";

interface Course {
  _id: string;
  title: string;
  shortDescription?: string;
  thumbnail?: string;
  price: number;
  currency?: string;
  discount?: number;
  originalPrice?: number;
  totalLessons?: number;
  enrolledStudents?: number;
  rating?: number;
  category?: string;
  instructor?: {
    firstName?: string;
    lastName?: string;
    name?: string;
  };
}

const Courses = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      const handleScroll = () => {
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
      };
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const res = await coursesApi.getCourses({ limit: 5 });
      console.log("Courses response:", res);
      if (res.success) {
        setCourses(res.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (dir: "left" | "right") => {
    const container = scrollRef.current;
    if (container) {
      container.scrollBy({ left: dir === "left" ? -350 : 350, behavior: "smooth" });
    }
  };

  const fmtPrice = (price: number, currency: string = "XAF") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + "k+";
    return num?.toString() || "0";
  };

  return (
    <section ref={sectionRef} className="py-16 lg:py-24 bg-gray-50">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="inline-block px-3 py-1.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full mb-3">
              Popular Courses
            </span>
            <h2 className="text-3xl font-bold text-gray-900 lg:text-4xl">
              Explore Our <span className="text-orange-500">Courses</span>
            </h2>
            <p className="max-w-lg mt-2 text-gray-600">
              Start learning from expert-led courses designed to help you succeed
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className="flex items-center justify-center w-10 h-10 transition-colors border-2 border-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange-500 hover:text-orange-500"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className="flex items-center justify-center w-10 h-10 transition-colors border-2 border-gray-200 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:border-orange-500 hover:text-orange-500"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex gap-6 pb-4 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {courses.map((course) => (
              <Link
                key={course._id}
                to={`/course/${course._id}`}
                className="flex-shrink-0 w-[300px] bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all group overflow-hidden"
              >
                {/* Image */}
                <div className="relative h-40 bg-gray-100">
                  {course.thumbnail ? (
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <BookOpen className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 text-xs font-medium text-gray-700 rounded-full bg-white/90">
                      {course.category || "course"}
                    </span>
                  </div>
                  <div className="absolute px-2 py-1 text-sm font-bold text-white bg-orange-500 rounded-lg bottom-3 right-3">
                    {course.price === 0 ? "Free" : fmtPrice(course.price, course.currency)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h6 className="mb-2 font-semibold text-gray-900 transition-colors line-clamp-2 group-hover:text-orange-600">
                    {course.title}
                  </h6>

                  <p className="mb-3 text-sm text-gray-500 line-clamp-2">
                    {course.shortDescription || "Learn new skills with this comprehensive course"}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      <span>{formatNumber(course.enrolledStudents || 0)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-400" />
                      <span>{course.rating?.toFixed(1) || "0.0"}</span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      {course.instructor?.firstName ? `${course.instructor.firstName} ${course.instructor.lastName || ""}` : course.instructor?.name || "LearnHub"}
                    </span>
                    <span className="flex items-center gap-1 text-sm font-medium text-orange-600 transition-all group-hover:gap-2">
                      View <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* View All Link */}
        <div className="mt-10 text-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 px-6 py-3 font-medium text-white transition-all bg-gray-900 rounded-full hover:bg-gray-800"
          >
            View All Courses
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Courses;